// Note on ARM:  WHEN MIGRATING OFF OF ANGULAR, THIS FILE CAN BE BROKEN DOWN FURTEHR TO MOVE RELEVANT INTO CONTENT TYPE SPECIFIC MODULE. E.G. flow related code would go in revisionHistoryFlow module
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { DatasourceContentType, FlowContentType, IContentType, WorkbookContentType } from '../../ts/models/ContentTypes';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { Set } from '../../ts/models/Set';
import { Module } from '../../ts/Module';
import { Flows } from '../../ts/resources/Flows';
import { Workbooks } from '../../content/resources/Workbooks';
import { RevertFlowResult } from '../../ts/ServerApi/requests/RevertFlowRequest';
import { IRevertWorkbookResult } from '../../ts/ServerApi/requests/RevertWorkbookRequest';
import { IContentRevision } from '../serverApi/types/IContentRevision';
import { IWorkbookRevision } from '../../revisionHistoryWorkbook/serverApi/types/IWorkbookRevision';
import { FlowRevision } from '../../revisionHistoryFlow/serverApi/types/FlowRevision';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { CheckConnectionCredentialsAction } from '../../ts/services/CheckConnectionCredentialsAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from '../../ts/services/ConfirmActionDialog';
import { DownloadActions } from '../../ts/services/download/DownloadActions';
import { ModalSize } from '../../ts/services/ModalService';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';

export class RevisionHistoryAction {

  public static $inject = [
    '$q',
    '$translate',
    '$rootScope',
    'CheckConnectionCredentialsAction',
    'ConfirmActionDialog'];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private $rootScope: ng.IRootScopeService,
    private CheckConnectionCredentialsAction: CheckConnectionCredentialsAction,
    private ConfirmActionDialog: ConfirmActionDialog) {
  }

  private showContentRevisionHistory(content: INamedItemInfo, contentType: IContentType): ng.IPromise<IResult> {

    const scope = <RevisionHistoryAction.Scope>this.$rootScope.$new(true);
    scope.contentItemId = content.id;
    scope.contentType = contentType;
    scope.selectedItems = new Set<IContentRevision>((revision: IContentRevision) => revision.id);

    let action; // 10.0 dialog action for workbooks should be one-click revert. Otherwise it's download revision.
    let actionName: string;
    let promptTemplate: string;
    switch (contentType.name) {
      case WorkbookContentType.name:
        action = this.getRevertWorkbookAction(scope);
        actionName = this.$translate.instant('revisionHistoryDialogSubmitRestore');
        promptTemplate = 'revisionHistoryDialogWorkbookDescription';
        break;
      case DatasourceContentType.name:
        action = this.getDownloadRevisionAction(scope);
        actionName = this.$translate.instant('revisionHistoryDialogSubmitDownload');
        promptTemplate = 'revisionHistoryDialogDatasourceDescription';
        break;
      case FlowContentType.name:
        action = this.getRevertFlowAction(scope);
        actionName = this.$translate.instant('revisionHistoryDialogSubmitRestore');
        promptTemplate = 'revisionHistoryDialogFlowDescription';
        break;
      default:
        throw new Error(this.$translate.instant('revisionHistoryUnsupportedContentType'));
    }

    scope.prompt = this.$translate.instant(promptTemplate, {
      NAME: L10n.elideIfLong(content.name)
    });

    return this.ConfirmActionDialog.open({
      scope: scope,
      title: this.$translate.instant('revisionHistoryDialogTitle'),
      bodyTemplateUrl: 'revisionHistoryDialog.html',
      actionName: actionName,
      action: action,
      confirmDisabled: () => {
        return scope.selectedItems && scope.selectedItems.size() !== 1;
      },
      size: ModalSize.large
    }).result;
  }

  private getRevertWorkbookAction(scope: RevisionHistoryAction.Scope) {
    return (dialogInstance: IConfirmActionDialogInstance) => {
      const revision: IWorkbookRevision = <IWorkbookRevision>scope.selectedItems.first();

      return Workbooks.revertWorkbook(scope.contentItemId, revision.versionNumber)
        .then((result: IRevertWorkbookResult): ng.IPromise<IRevertWorkbookResult> => {
          if (result.failedConnection) {
            return this.CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision);
          } else {
            return this.$q.when(result);
          }
        })
        .then((result: IRevertWorkbookResult) => {
          if (ResultHelper.hasErrors(result)) {
            dialogInstance.showErrorMessage(this.$translate.instant('revisionHistoryActionErrorForWorkbook'));
          } else {
            ToasterService.instance.pop({ type: ToastType.Info, text: this.$translate.instant('revisionHistoryActionSuccessForWorkbook') });
            if (result.scheduledExtractCredentialsMayNotBeRestored) {
              ToasterService.instance.pop({ type: ToastType.Info, text: this.$translate.instant('revisionHistoryActionSuccessGoToConnections') });
            }
          }
        });
    };
  }

  private getDownloadRevisionAction(scope: RevisionHistoryAction.Scope) {
    return () => {
      DownloadActions.downloadContentRevision(scope.selectedItems.first());
      return this.$q.when();
    };
  }

  private getRevertFlowAction(scope: RevisionHistoryAction.Scope) {
    return (dialogInstance: IConfirmActionDialogInstance) => {
      const revision: FlowRevision = <FlowRevision>scope.selectedItems.first();
      return Flows.revertFlow(scope.contentItemId, revision.versionNumber)
        .then((result: RevertFlowResult): ng.IPromise<RevertFlowResult> => {
          if (result.failedConnection) {
            return this.CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision);
          } else {
            return this.$q.when(result);
          }
        })
        .then((result: RevertFlowResult) => {
          if (ResultHelper.hasErrors(result)) {
            dialogInstance.showErrorMessage(this.$translate.instant('revisionHistoryActionErrorForFlow'));
          } else {
            ToasterService.instance.pop({ type: ToastType.Info, text: this.$translate.instant('revisionHistoryActionSuccessForFlow') });
          }
        });
    };
  }

  public showDataSourceRevisionHistory(datasource: INamedItemInfo): ng.IPromise<any> {
    return this.showContentRevisionHistory(datasource, DatasourceContentType);
  }

  public showWorkbookRevisionHistory(workbook: INamedItemInfo): ng.IPromise<any> {
    return this.showContentRevisionHistory(workbook, WorkbookContentType);
  }

  public showFlowRevisionHistory(flow: INamedItemInfo): ng.IPromise<any> {
    return this.showContentRevisionHistory(flow, FlowContentType);
  }
}

export module RevisionHistoryAction {

  export interface Scope extends ng.IScope {
    contentItemId: string;
    contentType: IContentType;
    revisionCount: number;
    selectedItems: Set<IContentRevision>;
    prompt: string;
  }

}

Module.instance().service('RevisionHistoryAction', RevisionHistoryAction);
